import { Controller } from "@hotwired/stimulus"
import { i18n } from "../lib/i18n"
import {
  blankRule,
  conditionFromRule,
  describeRule,
  ruleFromCondition,
  type LogicQuestion,
  type Rule,
  type StoredConditions,
} from "../lib/display_logic"
import { rulesTemplate, type PillPrototype } from "../templates/display_logic_rules"

// "Who sees this": Everyone, or only people whose earlier answers match a
// set of rules. Keeps the rules in memory and writes the complete ones to the
// hidden `display_conditions` field, firing `change` so the settings rail
// auto-saves.
//
// Connects to data-controller="conditional-display"
export default class extends Controller<HTMLElement> {
  static targets = ["hiddenField", "modeButton", "everyone", "rules", "summary", "pillTemplate"]
  static values = {
    surveyId: Number,
    // The question these rules gate; only questions before it are offered.
    questionId: Number,
    // A section's position; only questions in earlier sections are offered.
    sectionPosition: { type: Number, default: -1 },
    subject: { type: String, default: "question" },
  }

  declare readonly hiddenFieldTarget: HTMLInputElement
  declare readonly modeButtonTargets: HTMLButtonElement[]
  declare readonly everyoneTarget: HTMLElement
  declare readonly rulesTarget: HTMLElement
  declare readonly summaryTarget: HTMLElement
  declare readonly pillTemplateTarget: HTMLTemplateElement
  declare readonly surveyIdValue: number
  declare readonly questionIdValue: number
  declare readonly sectionPositionValue: number
  declare readonly subjectValue: string

  private questions: LogicQuestion[] = []
  private rules: Rule[] = []
  private join: "AND" | "OR" = "AND"
  private mode: "everyone" | "some" = "everyone"
  // The rule whose question list is open. A rule with no question yet always
  // shows its list.
  private openPicker: number | null = null
  private boundOutsideClick = this.onOutsideClick.bind(this)

  disconnect() {
    document.removeEventListener("click", this.boundOutsideClick)
  }

  async connect() {
    document.addEventListener("click", this.boundOutsideClick)
    const stored = this.storedConditions()
    this.join = stored?.operator === "OR" ? "OR" : "AND"
    this.mode = stored?.conditions?.length ? "some" : "everyone"
    // Read before the questions load too, so a mode click meanwhile writes
    // the stored rules back rather than none.
    this.rules = this.readRules(stored)
    this.renderMode()

    this.questions = await this.loadQuestions()
    this.rules = this.readRules(stored)
    // Opening the panel must not rewrite (and auto-save) the stored rules.
    this.render({ save: false })
  }

  setMode(event: Event) {
    this.mode = (event.currentTarget as HTMLElement).dataset.mode === "some" ? "some" : "everyone"
    this.renderMode()
    this.write()
  }

  setJoin(event: Event) {
    this.join = (event.currentTarget as HTMLSelectElement).value === "OR" ? "OR" : "AND"
    this.write()
  }

  addRule() {
    this.rules.push(blankRule())
    this.openPicker = null
    this.render()
    this.rulesTarget.querySelector<HTMLElement>(`[data-focus-key="${this.rules.length - 1}:qsearch"]`)?.focus()
  }

  removeRule(event: Event) {
    this.rules.splice(this.index(event), 1)
    this.openPicker = null
    if (this.rules.length === 0) {
      this.rules = [blankRule()]
      this.mode = "everyone"
      this.renderMode()
    }
    this.render()
  }

  togglePicker(event: Event) {
    const index = this.index(event)
    this.openPicker = this.openPicker === index ? null : index
    this.render()
    if (this.openPicker === index) this.rulesTarget.querySelector<HTMLElement>(`[data-focus-key="${index}:qsearch"]`)?.focus()
  }

  // Picking a different question starts its answer part over, to suit the
  // new question's type. Re-picking the same one keeps the rule.
  pickQuestion(event: Event) {
    const index = this.index(event)
    const question = this.question(Number((event.currentTarget as HTMLElement).dataset.questionId))
    this.openPicker = null
    if (question && question.id !== this.rules[index].questionId) this.rules[index] = blankRule(question)
    this.render()
    this.rulesTarget.querySelector<HTMLElement>(`[data-focus-key="${index}:question"]`)?.focus()
  }

  closePicker() {
    if (this.openPicker === null) return
    const index = this.openPicker
    this.openPicker = null
    this.render()
    this.focusable().find((element) => element.dataset.focusKey === `${index}:question`)?.focus()
  }

  // A click anywhere but the open list or its question button closes the list.
  private onOutsideClick(event: MouseEvent) {
    if (this.openPicker === null) return
    const target = event.target as Element | null
    if (!target?.isConnected) return
    const inside = target.closest(`[data-question-picker="${this.openPicker}"], [data-focus-key="${this.openPicker}:question"]`)
    if (inside && this.rulesTarget.contains(inside)) return
    this.openPicker = null
    this.render()
  }

  // Typing narrows the list and highlights its first match, so Enter picks it.
  filterQuestions(event: Event) {
    const search = event.currentTarget as HTMLInputElement
    const term = search.value.trim().toLowerCase()
    const list = this.rulesTarget.querySelector(`[data-question-list="${this.index(event)}"]`)
    const rows = Array.from(list?.querySelectorAll<HTMLElement>("[data-question-label]") ?? [])
    rows.forEach((row) => row.classList.toggle("hidden", !row.dataset.questionLabel!.includes(term)))
    list?.querySelectorAll("[data-section-heading]").forEach((heading) => heading.classList.toggle("hidden", term !== ""))
    list?.querySelector("[data-question-empty]")?.classList.toggle("hidden", rows.some((row) => !row.classList.contains("hidden")))
    this.highlight(search, this.pickableRows(search)[0])
  }

  // Arrow keys move the highlight, Enter picks it, Escape closes the list.
  questionKeydown(event: KeyboardEvent) {
    const search = event.currentTarget as HTMLInputElement
    const rows = this.pickableRows(search)
    const active = rows.findIndex((row) => row.hasAttribute("data-active"))

    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp": {
        event.preventDefault()
        const step = event.key === "ArrowDown" ? 1 : -1
        this.highlight(search, rows[(active + step + rows.length) % rows.length])
        break
      }
      case "Enter":
        // Never submit the settings form from the search box.
        event.preventDefault()
        rows[active]?.click()
        break
      case "Escape":
        event.preventDefault()
        this.closePicker()
        break
    }
  }

  setOp(event: Event) {
    const rule = this.edit(event)
    rule.op = (event.currentTarget as HTMLSelectElement).value
    this.render()
  }

  setOpChip(event: Event) {
    const rule = this.edit(event)
    rule.op = (event.currentTarget as HTMLElement).dataset.value!
    this.render()
  }

  toggleValue(event: Event) {
    const rule = this.edit(event)
    const value = (event.currentTarget as HTMLElement).dataset.value!
    // Yes and No together just means "answered"; Yes / No picks one.
    const single = this.question(rule.questionId)?.question_type === "boolean"
    if (rule.values.includes(value)) rule.values = rule.values.filter((v) => v !== value)
    else rule.values = single ? [value] : [...rule.values, value]
    this.render()
  }

  // A Yes / No answer: one pick, replacing a "skipped" rule.
  setAnswer(event: Event) {
    const rule = this.edit(event)
    rule.op = "any_of"
    rule.values = [(event.currentTarget as HTMLElement).dataset.value!]
    this.render()
  }

  setRating(event: Event) {
    const rule = this.edit(event)
    rule.value = (event.currentTarget as HTMLElement).dataset.value!
    this.render()
  }

  // Typing doesn't re-render, so the caret stays put.
  setField(event: Event) {
    const element = event.currentTarget as HTMLInputElement | HTMLSelectElement
    const rule = this.edit(event)
    const field = element.dataset.field as "value" | "value2" | "amount" | "unit"
    rule[field] = element.value
    this.write()
  }

  filterChoices(event: Event) {
    const search = event.currentTarget as HTMLInputElement
    const term = search.value.trim().toLowerCase()
    const list = this.rulesTarget.querySelector(`[data-choice-list="${this.index(event)}"]`)
    const rows = Array.from(list?.querySelectorAll<HTMLElement>("[data-choice-label]") ?? [])
    rows.forEach((row) => row.classList.toggle("hidden", !row.dataset.choiceLabel!.includes(term)))
    list?.querySelector("[data-choice-empty]")?.classList.toggle("hidden", rows.some((row) => !row.classList.contains("hidden")))
  }

  // Re-renders the rule cards, putting focus back on the control that had it.
  private render({ save = true } = {}) {
    const active = document.activeElement as HTMLElement | null
    const focusKey = active && this.rulesTarget.contains(active) ? active.dataset.focusKey : undefined
    this.rulesTarget.innerHTML = rulesTemplate({
      pills: this.pillPrototype(),
      rules: this.rules,
      questions: this.questions,
      eligible: this.eligibleQuestions(),
      join: this.join,
      openPicker: this.openPicker,
    })
    if (focusKey) this.focusable().find((element) => element.dataset.focusKey === focusKey)?.focus()
    // An open list starts on the current pick, else its first question.
    this.rulesTarget.querySelectorAll<HTMLInputElement>('input[role="combobox"]').forEach((search) => {
      const rows = this.pickableRows(search)
      this.highlight(search, rows.find((row) => row.getAttribute("aria-pressed") === "true") ?? rows[0])
    })
    if (save) this.write()
    else this.summaryTarget.textContent = this.summary()
  }

  private questionRows(search: HTMLInputElement): HTMLButtonElement[] {
    const list = this.rulesTarget.querySelector(`[data-question-list="${search.dataset.index}"]`)
    return Array.from(list?.querySelectorAll<HTMLButtonElement>("[data-question-label]") ?? [])
  }

  // The list's visible, enabled rows: instruction blocks can't be picked.
  private pickableRows(search: HTMLInputElement): HTMLButtonElement[] {
    return this.questionRows(search).filter((row) => !row.disabled && !row.classList.contains("hidden"))
  }

  private highlight(search: HTMLInputElement, row: HTMLElement | undefined) {
    this.questionRows(search).forEach((other) => other.toggleAttribute("data-active", other === row))
    if (row) {
      search.setAttribute("aria-activedescendant", row.id)
      row.scrollIntoView?.({ block: "nearest" })
    } else {
      search.removeAttribute("aria-activedescendant")
    }
  }

  // The server-rendered PillTabsComponent every rule-card switch is cloned from.
  private pillPrototype(): PillPrototype {
    const track = this.pillTemplateTarget.content.firstElementChild as HTMLElement
    return { track, pill: track.querySelector("button") as HTMLElement }
  }

  private focusable(): HTMLElement[] {
    return Array.from(this.rulesTarget.querySelectorAll<HTMLElement>("[data-focus-key]"))
  }

  private renderMode() {
    const some = this.mode === "some"
    this.modeButtonTargets.forEach((button) => {
      const on = (button.dataset.mode === "some") === some
      // PillTabsComponent lights the pill carrying data-active.
      button.toggleAttribute("data-active", on)
      button.setAttribute("aria-pressed", String(on))
    })
    this.everyoneTarget.classList.toggle("hidden", some)
    this.rulesTarget.classList.toggle("hidden", !some)
    this.summaryTarget.classList.toggle("hidden", !some)
  }

  private write() {
    const conditions = this.rules.map(conditionFromRule).filter((condition) => condition !== null)
    const value =
      this.mode === "some" && conditions.length > 0 ? JSON.stringify({ operator: this.join, conditions }) : ""

    this.summaryTarget.textContent = this.summary()
    if (value === this.hiddenFieldTarget.value) return

    this.hiddenFieldTarget.value = value
    this.hiddenFieldTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  private summary(): string {
    const sentences = this.rules
      .map((rule) => describeRule(rule, this.question(rule.questionId)))
      .filter((sentence): sentence is string => !!sentence)

    if (sentences.length === 0) return i18n.t("display_logic.summary_incomplete")

    const joiner = ` ${i18n.t(this.join === "AND" ? "display_logic.and" : "display_logic.or")} `
    return i18n.t(`display_logic.summary.${this.subjectValue}`, { rules: sentences.join(joiner) })
  }

  private eligibleQuestions(): LogicQuestion[] {
    const position = this.questions.findIndex((question) => question.id === this.questionIdValue)
    if (position >= 0) return this.questions.slice(0, position)
    if (this.sectionPositionValue >= 0) {
      return this.questions.filter((question) => question.section_position < this.sectionPositionValue)
    }
    return this.questions
  }

  private question(id: number | null): LogicQuestion | undefined {
    return this.questions.find((question) => question.id === id)
  }

  private index(event: Event): number {
    return Number((event.currentTarget as HTMLElement).dataset.index)
  }

  // The event's rule, which from now on saves as edited, not as stored.
  private edit(event: Event): Rule {
    const rule = this.rules[this.index(event)]
    delete rule.stored
    delete rule.unreadable
    return rule
  }

  private readRules(stored: StoredConditions | null): Rule[] {
    const rules = (stored?.conditions ?? []).map((condition) =>
      ruleFromCondition(condition, this.question(condition.question_id)),
    )
    return rules.length ? rules : [blankRule()]
  }

  private storedConditions(): StoredConditions | null {
    const raw = this.hiddenFieldTarget.value.trim()
    if (!raw) return null
    try {
      return JSON.parse(raw) as StoredConditions
    } catch {
      return null
    }
  }

  private async loadQuestions(): Promise<LogicQuestion[]> {
    try {
      const response = await fetch(`/surveys/${this.surveyIdValue}/questions.json`, {
        headers: { Accept: "application/json" },
      })
      if (!response.ok) return []
      const json = await response.json()
      return json.data ?? json
    } catch {
      return []
    }
  }
}
